import {
  type FC, useCallback, useMemo, useState,
} from 'react';
import { CONFIG } from '~/_shared/constants/config';
import { TwoFactorMode } from '~/_shared/constants/twoFactorMode.enum';
import { getTeamMemberError } from '~/clientTeamManagement/clientTeamManagement.helpers';
import {
  type AuthenticationResponse, sendTwoFactorEmailRequest,
} from '~/store/userData/repository/userData.repository';
import { useUserDataSelector } from '~/store/userData/userData.selectors';
import { useAuthContext } from '../../authContext';
import { Login2FaFormComponent } from './login2FaForm.component';
import { LoginFormComponent } from './loginForm.component';
import { useUserSign } from './useUserSign';

type LoginFormContainerProps = {
  onLoginFormSubmit?: () => void;
};

export const LoginFormContainer: FC<LoginFormContainerProps> = (props) => {
  const { isLoading, error, mfaMode, availableMfaModes, authenticateUserRequest, authenticate2FaUserRequest,
    resetTwoFactorData, changeMfaMode } = useUserSign();
  const { setAuthTokens, error: refreshTokenError, clearError } = useAuthContext();
  const teamMemberError = getTeamMemberError();
  const [usedEmail, setUsedEmail] = useState('');
  const [usedPassword, setUsedPassword] = useState('');
  const isLoadingFromState = useUserDataSelector().isLoading;
  const isAnythingLoading = isLoading || isLoadingFromState;

  const dispatchProps = useMemo(() => {
    const processAuthResponse = (response: void | AuthenticationResponse) => {
      if (response?.refresh && response?.access_token_expiration) {
        setAuthTokens(response.refresh, response.access_token_expiration);
      }
    };

    return ({
      onFormSubmit: async (email: string, password: string, rememberMe: boolean) => {
        clearError();
        setUsedEmail(email);
        setUsedPassword(password);
        const response = await authenticateUserRequest({ email, password, rememberMe });
        processAuthResponse(response);

        const onLoginFormSubmit = props.onLoginFormSubmit;
        onLoginFormSubmit?.();
      },
      on2FASubmit: async (code: string) => {
        clearError();
        const response = await authenticate2FaUserRequest(code);
        processAuthResponse(response);
      },
      reset2FaMode: () => {
        resetTwoFactorData();
      },
    });
  }, [authenticate2FaUserRequest, authenticateUserRequest, clearError, props.onLoginFormSubmit, resetTwoFactorData, setAuthTokens]);

  const resendMFAEmail = useCallback(() =>
    sendTwoFactorEmailRequest(usedEmail, usedPassword),
  [usedEmail, usedPassword]);

  const onLegacyLogin = useCallback(() => {
    location.href = CONFIG.LEGACY_LOGIN_URL;
  }, []);

  return (
    <div>
      {!mfaMode ? (
        <LoginFormComponent
          error={error || refreshTokenError || teamMemberError || ''}
          isLoading={isAnythingLoading}
          onSubmit={dispatchProps.onFormSubmit}
          onLegacyLogin={onLegacyLogin}
        />
      ) : (
        <Login2FaFormComponent
          error={error || refreshTokenError || ''}
          isLoading={isAnythingLoading}
          mfaMode={mfaMode}
          availableMfaModes={availableMfaModes}
          onSubmit={dispatchProps.on2FASubmit}
          repeatMFA={mfaMode === TwoFactorMode.google ? undefined : resendMFAEmail}
          reset2FaMode={dispatchProps.reset2FaMode}
          onChangeMfaMode={changeMfaMode}
        />
      )}
    </div>
  );
};
